import "Assets/styles/default/components/Product/QuotationModal/Syngenta.less"; 

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Col,
  Form,
  Row,
  VertButton,
  VertContainer,
  VertInput,
  VertModal,
  VertRadio,
  VertSelect,
  VertTypography,
  notification,
} from '@vertem-core/vertice-ds';
import { Crops } from 'enums/quotation';
import { useSelector } from 'react-redux';
import usePipeContext from 'Services/Providers/PipeProvider/usePipeContext';

// UTILS
import generateUUID from 'Utils/generateUUID';
import { StateList } from 'Utils/commons/address';
import { cpf } from 'cpf-cnpj-validator';

const SyngentaQuotationFormModal = ({
  product,
  vendorId,
  sku,
  originalId,
  detail,
  onRequestClose,
  onRequestSuccess,
  loading,
  setLoading,
  isOpen,
}) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [showNomeEtd, setShowNomeEtd] = useState(false);
  const [showNomeCanalDistribuicao, setShowNomeCanalDistribuicao] =
    useState(false);
  const quotation = useSelector(({ Quotation }) => Quotation);

  const [useForm] = Form.useForm();

  const userProfile = useSelector((state) => state.oidc.user?.profile);

  // @ts-ignore
  const { callApi } = usePipeContext();

  const handleConfirmationOpen = () => {
    setIsConfirmationOpen(true);
  };

  const handleConfirmationClose = () => {
    setIsConfirmationOpen(false);
    setIsSubmitLoading(false);
    onRequestSuccess(false);
    clearFields();
  };

  const handleChange = (value) => {
    const fieldsErros = useForm.getFieldsError();
    const hasValid = fieldsErros.find((a) => a.errors.length > 0)
      ? false
      : true;
  };

  const clearFields = () => {
    useForm.resetFields([
      'name',
      'cpf',
      'celular',
      'email',
      'nomeEtd',
      'nomeRtv',
      'nomeCanalDistribuicao',
      'nomeCompra',
      'tamanho',
      'estado',
      'tipoCultura',
      'compraProdutosSyngenta',
      'atendimento',
    ]);
  };

  useEffect(() => {
    if (quotation && quotation.isLoaded && quotation.idQuotation > 0) {
      handleConfirmationOpen();
      setIsSubmitLoading(false);
    }
    if (quotation.isError) {
      setIsSubmitLoading(false);
      notification.error({
        message:
          'Não foi possível registrar a Cotação, entre em contato com a central!',
      });
    }
  }, [quotation]);

  useEffect(() => {
    clearFields();
  }, [vendorId]);

  const handleSubmit = () => {
    useForm
      .validateFields()
      .then((a) => {
        const fieldsErros = useForm.getFieldsError();
        const hasValid = fieldsErros.find((a) => a.errors.length > 0);
        if (!hasValid) {
          setIsSubmitLoading(true);
          const products = [
            {
              name: product,
              sku: String(originalId),
              quantity: Number(useForm.getFieldValue('tamanho')),
              unitPrice: 0,
              vendorId: String(vendorId),
              originalId: String(originalId),
              formId: '2',
            },
          ];

          callApi({
            symbol: 'ADD_QUOTATION',
            alias: generateUUID(),
            payload: {
              products: products,
              supplierId: vendorId,
              participantId: userProfile['custom:participant_id'],
              params: [
                { key: 'Nome', value: useForm.getFieldValue('name') },
                { key: 'Telefone', value: useForm.getFieldValue('celular') },
                { key: 'CPF', value: useForm.getFieldValue('cpf') },
                { key: 'Email', value: useForm.getFieldValue('email') },
                {
                  key: 'Nome do RTV',
                  value: useForm.getFieldValue('nomeRtv') ?? '',
                },
                {
                  key: 'Nome do Canal de Distribuição',
                  value: useForm.getFieldValue('nomeCanalDistribuicao') ?? '',
                },
                {
                  key: 'Como você compra produtos da Syngenta?',
                  value: useForm.getFieldValue('compraProdutosSyngenta'),
                },
                {
                  key: 'Você é atendido por algum ETD?',
                  value: useForm.getFieldValue('atendimento'),
                },
                {
                  key: 'Nome do ETD',
                  value: useForm.getFieldValue('nomeEtd') ?? '',
                },
                {
                  key: 'Estado da Propriedade',
                  value: useForm.getFieldValue('estado'),
                },
                {
                  key: 'Tipo de Cultura',
                  value: useForm.getFieldValue('tipoCultura'),
                },
                {
                  key: 'Tamanho da área que deseja contratar (em hectares)',
                  value: useForm.getFieldValue('tamanho'),
                },
              ],
            },
          });
        }
      })
      .catch((b) => {
        console.log('erro cotação', b);
      });
  };

  const atendimentoChangeHandle = () => {
    setShowNomeEtd(
      useForm.getFieldValue('atendimento')?.toUpperCase() == 'SIM'
    );
    useForm.setFieldValue('nomeEtd', '');
  };

  const rtvChangeHandle = () => {
    if (
      useForm.getFieldValue('compraProdutosSyngenta')?.toUpperCase() == 'RTV'
    ) {
      setShowNomeCanalDistribuicao(false);
      useForm.setFieldValue('nomeCanalDistribuicao', '');
      return;
    }
    setShowNomeCanalDistribuicao(true);
    useForm.setFieldValue('nomeRtv', '');
  };

  return (
    <>
      <Form
        name="FormsPersonalData"
        form={useForm}
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        initialValues={{ remember: true }}
        autoComplete="off"
        layout="vertical"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-regular)',
        }}
        onValuesChange={(_, formChanged) => handleChange(formChanged)}
      >
        <VertContainer spacingX="small">
          <Row justify="start">
            <Col span={24}>
              <VertTypography.Paragraph strong>
                Dados Pessoais
              </VertTypography.Paragraph>
            </Col>
          </Row>

          <Row justify="start">
            <Col span={24}>
              <Form.Item
                name="name"
                validateTrigger={['onBlur', 'onChange']}
                rules={[
                  { required: true, message: 'Nome precisa ser preenchido.' },
                ]}
              >
                <VertInput label="Nome Completo*" maxLength={100} />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="start">
            <Col flex="auto">
              <Form.Item
                name="cpf"
                validateTrigger={['onBlur', 'onChange']}
                rules={[
                  { required: true, message: 'CPF precisa ser preenchido.' },
                  {
                    pattern: /^[0-9]{3}\.?[0-9]{3}\.?[0-9]{3}\-?[0-9]{2}$/g,
                    validator: (_, value) => {
                      if (value && !cpf.isValid(value)) {
                        return Promise.reject(new Error('CPF inválido.'));
                      }
                      return Promise.resolve();
                    },
                    message: 'CPF inválido.',
                  },
                ]}
              >
                <VertInput
                  label="CPF*"
                  template="marketplace"
                  mask="000.000.000-00"
                  placeholder=""
                  maskOptions={{
                    lazy: true,
                  }}
                />
              </Form.Item>
            </Col>
            <Col flex="auto">
              <Form.Item
                name="celular"
                validateTrigger={['onBlur', 'onChange']}
                rules={[
                  {
                    required: true,
                    message: 'Celular precisa ser preenchido.',
                  },
                  {
                    pattern: /^\([0-9]{2}\) [0-9]{5}-[0-9]{4}$/g,
                    message: 'Celular inválido.',
                  },
                ]}
              >
                <VertInput
                  mask="(00) 00000-0000"
                  placeholder=""
                  maskOptions={{
                    lazy: true,
                  }}
                  label="Celular*"
                />
              </Form.Item>
            </Col>
            <Col flex="auto">
              <Form.Item
                name="email"
                validateTrigger={['onBlur', 'onChange']}
                rules={[
                  { required: true, message: 'Email precisa ser preenchido.' },
                  {
                    type: 'email',
                    message: 'Email inválido.',
                  },
                ]}
              >
                <VertInput label="Email*" />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="start">
            <Col span={24}>
              <VertTypography.Paragraph strong>
                Dados de comportamento
              </VertTypography.Paragraph>
            </Col>
          </Row>

          <Row justify="start">
            <Col flex="auto">
              <Row justify="start">
                <Col flex="auto">
                  <VertTypography.Paragraph>
                    Como você compra produtos da sygenta?
                  </VertTypography.Paragraph>
                </Col>
              </Row>
              <Row justify="start">
                <Col flex="auto">
                  <Form.Item name="compraProdutosSyngenta" initialValue="RTV">
                    <VertRadio.Group onChange={() => rtvChangeHandle()}>
                      <VertRadio value="RTV">RTV</VertRadio>
                      <VertRadio value="Canal de distribuição">
                        Canal de distribuição
                      </VertRadio>
                    </VertRadio.Group>
                  </Form.Item>
                </Col>
              </Row>

              {showNomeCanalDistribuicao ? (
                <Row justify="start">
                  <Col span={24}>
                    <Form.Item
                      name="nomeCanalDistribuicao"
                      validateTrigger={['onBlur', 'onChange']}
                      rules={[
                        {
                          required: true,
                          message:
                            'Nome do Canal de Distrituição precisa ser preenchido.',
                        },
                      ]}
                    >
                      <VertInput
                        label="Nome do Canal de Distrituição"
                        maxLength={100}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              ) : (
                <Row justify="start">
                  <Col span={24}>
                    <Form.Item
                      name="nomeRtv"
                      validateTrigger={['onBlur', 'onChange']}
                      rules={[
                        {
                          required: true,
                          message: 'Nome do RTV precisa ser preenchido.',
                        },
                      ]}
                    >
                      <VertInput label="Nome do RTV" maxLength={100} />
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </Col>
            <Col flex="auto">
              <Row justify="start" align="bottom">
                <Col flex="auto">
                  <VertTypography.Paragraph>
                    Você é atendido por algum ETD?
                  </VertTypography.Paragraph>
                </Col>
              </Row>
              <Row justify="start" align="bottom">
                <Col flex="auto">
                  <Form.Item name="atendimento" initialValue="Não">
                    <VertRadio.Group onChange={() => atendimentoChangeHandle()}>
                      <VertRadio value="Sim">Sim</VertRadio>
                      <VertRadio value="Não">Não</VertRadio>
                    </VertRadio.Group>
                  </Form.Item>
                </Col>
              </Row>

              {showNomeEtd && (
                <Row justify="start">
                  <Col span={24}>
                    <Form.Item name="nomeEtd">
                      <VertInput label="Nome do ETD" maxLength={100} />
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </Col>
          </Row>
          <Row justify="start">
            <Col flex="auto">
              <VertTypography.Paragraph strong>
                Informações para cotação
              </VertTypography.Paragraph>
            </Col>
          </Row>
          <Row justify="start" verticalSpacing="small">
            <Col span={24}>
              <VertInput label="Produto" value={product} disabled={true} />
            </Col>
          </Row>
          <Row className="syngenta-quotation" justify="start">
            <Col span={12}>
              <Form.Item
                name="estado"
                label="Estado da propriedade"
                validateTrigger={['onBlur', 'onChange']}
                rules={[
                  {
                    required: true,
                    message: 'Estado da propriedade precisa ser preenchido.',
                  },
                ]}
              >
                <VertSelect className="state-selector" items={StateList} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="tipoCultura"
                validateTrigger={['onBlur', 'onChange']}
                label="Tipo de cultura"
                rules={[
                  {
                    required: true,
                    message: 'Tipo de cultura precisa ser preenchido.',
                  },
                ]}
              >
                <VertSelect className="culture-selector" items={Crops} />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="start">
            <Col span={24}>
              <Form.Item
                name="tamanho"
                validateTrigger={['onBlur', 'onChange']}
                rules={[
                  {
                    required: true,
                    message: 'Tamanho da área precisa ser preenchido.',
                  },
                ]}
              >
                <VertInput
                  label="Tamanho da área que deseja contratar (em hectares)"
                  type="number"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="start">
            <Col span={24}>
              <VertButton
                type="highlight"
                size="large"
                onClick={handleSubmit}
                key="submit"
                htmlType="submit"
                loading={isSubmitLoading}
              >
                Enviar
              </VertButton>
            </Col>
          </Row>
        </VertContainer>
      </Form>
      <VertModal
        open={isConfirmationOpen}
        className="quotation-options-modal-confirmation"
        onCancel={handleConfirmationClose}
        spacing="regular"
      >
        <VertContainer>
          <Row justify="center" align="middle" className="info-primary">
            <VertTypography.Paragraph strong>
              Cotação enviada com sucesso
            </VertTypography.Paragraph>
          </Row>
          <Row verticalSpacing="small">
            <Col>
              <VertTypography.Text>
                Agradecemos o contato. Em breve enviaremos a sua cotação para o
                seu email de cadastro
              </VertTypography.Text>
            </Col>
          </Row>
          <VertTypography.Text>
            Solicitação: {quotation?.idQuotation}
          </VertTypography.Text>
          <Row justify="center" align="middle">
            <VertButton
              type="highlight"
              size="large"
              onClick={handleConfirmationClose}
            >
              Entendi
            </VertButton>
          </Row>
        </VertContainer>
      </VertModal>
    </>
  );
};

SyngentaQuotationFormModal.propTypes = {
  isVisible: PropTypes.bool,
  originalId: PropTypes.string,
  sku: PropTypes.string,
  detail: PropTypes.object,
  onRequestClose: PropTypes.func,
  onRequestSuccess: PropTypes.func,
};

export default SyngentaQuotationFormModal;
